import { find, get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { grey, navy, white } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import Stars from '@zocdoc/components/src/stars';
import { FontFamily } from '@zocdoc/components/src/typography';

import { PROVIDER_RESULT_TYPES } from '../../constants';
import { LOCALIZED_STRINGS } from '../../DoctorCard/constants';
import { isCommunity } from '../../../../../../Shared/utils/communityLabel';
import { titlesFrom } from '../../../../../../Shared/utils/institutesAndTitles';

const FALLBACK_IMAGE = (
    <svg
        data-test="doctor-card-photo-image-fallback"
        viewBox="0 0 110 110"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M21.379 79.863c3.054-2.271 7.009-3.015 10.506-4.385a45.11 45.11 0 0 0 4.657-2.08 24.241 24.241 0 0 0 18.457 8.035 24.245 24.245 0 0 0 18.46-8.034 45.883 45.883 0 0 0 4.658 2.08c3.496 1.369 7.451 2.114 10.505 4.384 3.666 2.725 5.444 7.813 6.548 12.686a55 55 0 1 0-80.339.001c1.104-4.873 2.882-9.962 6.548-12.687zM55 20c17.126 0 24.648 11.988 22.826 27.916-1.478 12.93-7.128 28.669-22.826 28.67-15.7.002-21.346-15.741-22.826-28.67C30.352 31.987 37.877 20 55 20z"
            fill="#f3f3f4"
        />
        <path
            d="M32.174 47.916C30.352 31.987 37.877 20 55 20c17.126 0 24.648 11.988 22.826 27.916-1.478 12.93-7.128 28.669-22.826 28.67-15.7.002-21.346-15.741-22.826-28.67zM55 110a54.836 54.836 0 0 0 40.17-17.45c-1.104-4.874-2.882-9.962-6.548-12.687-3.054-2.27-7.009-3.015-10.505-4.384a45.883 45.883 0 0 1-4.658-2.08 24.245 24.245 0 0 1-18.46 8.034 24.241 24.241 0 0 1-18.457-8.035 45.11 45.11 0 0 1-4.657 2.08c-3.497 1.37-7.452 2.114-10.506 4.385-3.666 2.725-5.444 7.814-6.548 12.688A54.836 54.836 0 0 0 55 110z"
            fill="#e1e1e4"
        />
    </svg>
);

// caching the array to help prevent memory pressure
const FALLBACK_ARRAY = [];
const getTier = data => get(find(get(data, 'provider.selectedInsurance.properties', FALLBACK_ARRAY), { key: 'tier' }), 'value');

export class BaseDoctorCard extends React.PureComponent {
    static propTypes = {
        /**
         * From GQL withSelectedInsurance()
         */
        data: PropTypes.shape({
            provider: PropTypes.shape({
                selectedInsurance: PropTypes.shape({
                    properties: PropTypes.array,
                }),
            }),
        }),

        insuranceImageUrl: PropTypes.string,
        intl: PropTypes.object,
        isSponsoredResult: PropTypes.bool,

        location: PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            address3: PropTypes.string,
            city: PropTypes.string,
            distance: PropTypes.number,
            name: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
        }).isRequired,

        locationId: PropTypes.string.isRequired,

        provider: PropTypes.shape({
            approvedFullName: PropTypes.string,
            averageRating: PropTypes.number,
            circlePictureUrl: PropTypes.string,
            firstName: PropTypes.string,
            isPreview: PropTypes.bool,
            lastName: PropTypes.string,
            postnominal: PropTypes.string,
            prenominal: PropTypes.string,
            profileUrl: PropTypes.string,

            relevantSpecialty: PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            }).isRequired,

            representativeReview: PropTypes.shape({
                comment: PropTypes.string,
            }),

            squarePictureUrl: PropTypes.string,
        }).isRequired,

        providerId: PropTypes.string.isRequired,
        providerResultType: PropTypes.oneOf(Object.keys(PROVIDER_RESULT_TYPES)),

        theme: PropTypes.shape({
            search: PropTypes.shape({
                showCommunityLabel: PropTypes.bool,
                showInstitutes: PropTypes.bool,
            }),
        }),
    };

    static defaultProps = {
        isSponsoredResult: false,
        providerResultType: PROVIDER_RESULT_TYPES.IN_NETWORK,
    };

    render() {
        const {
            data,
            intl,
            location,
            provider,
            providerResultType,
            isSponsoredResult,
            theme,
            ...props
        } = this.props;

        const tier = getTier(data);
        const { showCommunityLabel, showInstitutes } = theme.search;

        return (
            <DoctorCardContainer
                {...props}
                data-is-sponsored-result={isSponsoredResult}
                data-test="doctor-card"
                href={provider.profileUrl}
                title={intl.formatMessage(LOCALIZED_STRINGS.ViewProfile, { name: provider.approvedFullName })}
            >
                <DoctorCardPhotoContainer data-test="doctor-card-photo">
                    {this.maybeRenderPhoto(provider)}

                    {showCommunityLabel && isCommunity(provider) && (
                        <CommunityLabel data-test="community-label">
                            COMMUNITY
                        </CommunityLabel>
                    )}
                    {provider.averageRating > 0 && (
                        <DoctorCardInfoRatingStars
                            data-test="doctor-card-info-rating-stars"
                            rating={provider.averageRating}
                        />
                    )}
                </DoctorCardPhotoContainer>

                <DoctorCardInfoContainer data-test="doctor-card-info">
                    <DoctorCardInfoTopBar>
                        <DoctorCardInfoName
                            data-test="doctor-card-info-name"
                            data-uem-id="doctor-card-info-name"
                        >
                            {provider.prenominal ? (
                                <span
                                    data-test="doctor-card-info-name-prefix"
                                    itemProp="honorificPrefix"
                                >
                                    {provider.prenominal}&nbsp;
                                </span>
                            ) : null}

                            <span
                                data-test="doctor-card-info-name-full"
                                itemProp="name"
                            >
                                {provider.firstName} {provider.lastName}
                            </span>

                            {provider.postnominal ? (
                                <span>
                                    ,&nbsp;
                                    <span
                                        data-test="doctor-card-info-name-suffix"
                                        itemProp="honorificSuffix"
                                    >
                                        {provider.postnominal}
                                    </span>
                                </span>
                            ) : null}
                        </DoctorCardInfoName>
                    </DoctorCardInfoTopBar>

                    {
                        provider.relevantSpecialty && provider.relevantSpecialty.name && (
                            <DoctorCardInfoSpecialty
                                data-test="doctor-card-info-specialty"
                                itemProp="jobTitle"
                            >
                                {provider.relevantSpecialty.name}
                            </DoctorCardInfoSpecialty>
                        )
                    }

                    {showInstitutes && titlesFrom(provider) && (
                        <DoctorCardTitles
                            alt={titlesFrom(provider)}
                            data-test="doctor-card-titles"
                        >
                            {titlesFrom(provider)}
                        </DoctorCardTitles>
                    )}

                    <DoctorCardInfoLocationContainer>
                        <DoctorCardInfoLocation
                            data-test="doctor-card-info-location"
                            itemProp="workLocation"
                        >
                            {this.maybeRenderFancyLocationName(location.name)}

                            <span
                                data-test="doctor-card-info-location-address"
                                itemProp="streetAddress"
                            >
                                {[
                                    location.address1,
                                    location.address2,
                                    location.address3,
                                ].filter(Boolean).join(', ')}
                            </span>

                            <span>
                                <span
                                    data-test="doctor-card-info-location-city"
                                    itemProp="addressLocality"
                                >
                                    {location.city}
                                </span>
                                <span
                                    data-test="doctor-card-info-location-state"
                                    itemProp="addressRegion"
                                >
                                    {location.state}
                                </span>
                                <span
                                    data-test="doctor-card-info-location-zip"
                                    itemProp="postalCode"
                                >
                                    {location.zipCode}
                                </span>
                            </span>
                        </DoctorCardInfoLocation>

                        {tier ? (
                            <NetworkLevel data-test="doctor-card-info-network-level">
                                Network level: {tier}
                            </NetworkLevel>
                        ) : null}
                    </DoctorCardInfoLocationContainer>
                </DoctorCardInfoContainer>
            </DoctorCardContainer>
        );
    }

    maybeRenderPhoto({ circlePictureUrl, squarePictureUrl }) {
        if (circlePictureUrl || squarePictureUrl) {
            if (!circlePictureUrl || circlePictureUrl === squarePictureUrl) {
                return (
                    <DoctorCardPhotoMask data-test="doctor-card-photo-mask">
                        <DoctorCardPhoto
                            data-test="doctor-card-photo-image"
                            itemProp="image"
                            role="presentation"
                            src={squarePictureUrl}
                        />
                    </DoctorCardPhotoMask>
                );
            }

            return (
                <DoctorCardPhoto
                    data-test="doctor-card-photo-image"
                    itemProp="image"
                    role="presentation"
                    src={circlePictureUrl}
                />
            );
        }

        return FALLBACK_IMAGE;
    }

    maybeRenderFancyLocationName(name) {
        if (name) {
            return (
                <div
                    data-test="doctor-card-info-location-name"
                    itemProp="alternateName"
                >
                    {name}
                </div>
            );
        }
    }
}

const DoctorCardContainer = styled.a`
    display: flex;
    padding: 4px 0 0;
    vertical-align: top;
    cursor: pointer;
    position: relative;
`;

const PHOTO_SIZE_COMPACT = 60;

const DoctorCardPhotoContainer = styled.div`
    align-items: center;
    display: flex;
    flex: 0 0 ${PHOTO_SIZE_COMPACT}px;
    flex-direction: column;
    margin-right: 15px;
`;

const DoctorCardPhotoMask = styled.div`
    align-items: center;
    border-radius: 50%;
    border-color: ${white};
    display: flex;
    overflow: hidden;
    height: ${PHOTO_SIZE_COMPACT}px;
    width: ${PHOTO_SIZE_COMPACT}px;
`;

const DoctorCardPhoto = styled.img`
    width: ${PHOTO_SIZE_COMPACT}px;
`;

const DoctorCardInfoContainer = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;

    ${breakpoint.medium} {
        padding-top: 0;
    }
`;

const DoctorCardInfoTopBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
`;

const DoctorCardInfoName = styled.div`
    color: ${navy};
    font: 16px/1.4 ${FontFamily.SharpSansBold};

    > * {
        border-bottom: 1px dotted;
    }
`;

const DoctorCardInfoSpecialty = styled.div`
    margin-top: 7px;
    color: ${grey};
    font: 12px/1.25 ${FontFamily.SharpSansSemibold};
`;

const DoctorCardTitles = styled(DoctorCardInfoSpecialty)`
    margin-top: 4px;
    line-height: 1.2em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DoctorCardInfoRatingStars = styled(Stars)`
    font-size: 10px;
    margin-top: 9px;
`;

const DoctorCardInfoLocationContainer = styled.div`
    span:not(:last-of-type)::after {
        content: ', ';
    }
    color: ${navy};
    font: 12px/16px ${FontFamily.SharpSansSemibold};
    margin-top: 6px;
`;

const DoctorCardInfoLocation = styled.div`
    margin-right: 10px;
`;

const NetworkLevel = styled.div`
    color: ${navy};
`;

const CommunityLabel = styled.div`
    color: ${navy};
    font: 10px ${FontFamily.SharpSansBold};
    text-align: center;
    margin-top: 4px;
`;

export default injectIntl(BaseDoctorCard);
